/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react"
import {toast} from "react-hot-toast";
import {usePostRequest} from "../../../../api/helper";
import ArgosButton from "../../Buttons/ArgosButton";
import ArgosFlexbox from "../../Containers/ArgosFlexbox";
import ArgosMenuItem from "../../Form/Select/ArgosMenuItem";
import Loader from "../../Loader/Loader";
import InputLabel from '@mui/material/InputLabel';
import ListItemText from "@mui/material/ListItemText";
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {
  CAPABILITY_TYPE_STR,
  getOptionsListForType,
  OBJECT_TYPE_STR,
  PACKAGE_TYPE_STR, ROAD_TYPE_TYPE_STR,
  USE_CASE_TYPE_STR
} from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/constants";
import {IFetchObjectsResponse, IFullObjectData, IProcessedObjectData, TPmoAndMiscProfileNames} from "../../../../constants/objects/ObjectTypes/common";
import {ISignVariant} from "../../../../constants/objects/ObjectTypes/sign";
import {cloneDeep} from "lodash";
import useFullObjectData from "../../../../hooks/objects/useFullObjectData";
import {getProfileDisplayName, getSignVariantNameFromWidthHeight, processObjectData, sortObjectData} from "../../../../helpers/objects/helpers";
import ADAS_PROGRAMS_CONSTANTS from "../../../pages/AdasPrograms/constants";
import ArgosTextField from "../../Form/TextField/ArgosTextField";
import {selectCoverageRequirementsDataState} from "../../../../redux/ducks/CoverageRequirements/slice";
import {useAppSelector} from "../../../../redux/hooks";
import { tallyVersionsCount } from "./AddPropertyDialog";
import {validateURL} from "../../../../helpers/url/urlHelpers";

interface Props {
  propertyType: string;
  data: any;
  onSave: any;
  onClose: any;
  hideSaveAndCancel?: boolean;

  // Bulk Edits
  configState?: any;
  setBulkConfig?: Function;
  title?: string;
}

const EditPropertyDialog: React.FC<Props> = (props) => {
  const [isLoadingObjectData, setIsLoadingObjectData] = useState(false)

  const selectedDatasetPropertyType: string = props.propertyType

  /**
   * Object property state
   */
    // State of dropdowns
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [selectedName, setSelectedName] = useState<string>("");
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  type SelectedVariantType = string | TPmoAndMiscProfileNames;
  const [selectedVariant, setSelectedVariant] = useState<SelectedVariantType>("");

  // Dropdown change handler
  const handleVariantChange = (event: any) => {
    setSelectedVariant(event.target.value);
  };
  // Fetch object data when the component mounts
  useEffect(() => {
    if (selectedDatasetPropertyType === OBJECT_TYPE_STR) {
      fetchObjectData()
    }
  }, [])
  // Setup for fetching object data
  const fetchObjectsHandle = usePostRequest('/HttpTriggerCoverageRequirements?request_operation=get&request_type=getRwoData')

  const fetchObjectData = () => {
    setIsLoadingObjectData(true)
    fetchObjectsHandle().then((fetchObjectsResponse: IFetchObjectsResponse) => {
      const processedObjectData = processObjectData(fetchObjectsResponse)
      const sortedProcessedObjectData = sortObjectData(processedObjectData)
      setObjCatToData(sortedProcessedObjectData);
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to load object data")
    }).finally(() => {
      setIsLoadingObjectData(false)
    })
  }
  // State to store the processed object data
  const [objCatToData, setObjCatToData] = useState<IProcessedObjectData>({})

  // Setup for processing object data
  // Get relevant object data given what is selected in dropdowns and using the processed object data (convert objects from backend into a more usable format)
  const fullObjectData = useFullObjectData(selectedCategory, selectedName, selectedVersion, selectedVariant, objCatToData);
  // Prepopulate dropdowns using previously selected variant ID
  useEffect(() => {
    if (props.data?.rowData && objCatToData) {
      const objectId = props.data.rowData.objectId
      if (!objectId) {
        return
      }
      // TODO: Get the category, name, version, and variant from the object data given the object ID
      const _fullObjectData: IFullObjectData | null = findObjectDataByVariantId(objectId, objCatToData)
      if (!_fullObjectData) {
        return
      }
      const {variantId} = _fullObjectData;
      if (variantId !== objectId) {
        return
      }

      const category = _fullObjectData.fullObjectData.type

      let name = ''
      if (category === "Miscellaneous") {
        name = _fullObjectData.fullObjectData.objectName
      } else if (category === "Potentially Moving Object") {
        name = _fullObjectData.fullObjectData.objectName
      } else if (category === "Sign") {
        name = _fullObjectData.fullObjectData.signName
      }

      const version = _fullObjectData.fullObjectData.latestVersion
      const variant = _fullObjectData.selectedVariant

      setSelectedCategory(category)
      setSelectedName(name)
      setSelectedVersion(version)
      setSelectedVariant(variant)
    }
  }, [props.data, objCatToData]);

  // Get the object categories to display in the dropdown
  const objCats = Object.keys(objCatToData)

  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(props.data.id)
  /**
   * Save the selected property data to redux (adding to driving scenario properties).
   */
  const saveData = () => {
    if (selectedDatasetPropertyType !== OBJECT_TYPE_STR) {
      const newObj = formatData(selectedPropertyId)
      props.onSave(newObj, false, props?.data.index)
    } else {
      // Identify the link for the option if it exists
      let link: any = ""
      // Save new property object
      let newObj: any = {}
      newObj['type'] = selectedDatasetPropertyType // property type eg Object, Road Type, Package
      newObj['category'] = selectedCategory // this is only for objects
      newObj['name'] = selectedName
      newObj['variantName'] = selectedVariant
      newObj['objectId'] = fullObjectData?.variantId
      newObj['property_link'] = link
      if (props.onSave) {
        props.onSave(cloneDeep(newObj), false, props.data.index)
      }
    }

    props.onClose()
  }

  const coverageRequirementsData = useAppSelector(selectCoverageRequirementsDataState)
  /**
   * This function checks the configState or coverageRequirementsData to find objects that have already been selected.
   * @returns {Array} - Returns an array of already selected objects based on the selected dataset property type.
   */
  const checkForAlreadySelectedObjects = () => {
    if (props.configState) {
      return props.configState[selectedDatasetPropertyType]?.filter((prop: any) => prop.type === (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? ROAD_TYPE_TYPE_STR : OBJECT_TYPE_STR))
    }
    // Objects already added to the table but not yet saved to the database
    else if (coverageRequirementsData && coverageRequirementsData.data) {
      return coverageRequirementsData.data.driving_scenario_properties.filter((prop: any) => prop.type === (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? ROAD_TYPE_TYPE_STR : OBJECT_TYPE_STR))
    } else {
      return []
    }
  }

  const alreadySelectedObjects = checkForAlreadySelectedObjects()

  const handleOptionsChange = (event: any) => {
    const value = event.target.value
    // setSelectedOption(value)
    setSelectedPropertyId(value)
    if (props.setBulkConfig) {
      props.setBulkConfig(formatData(value))
    }
  }

  const [propertyLink, setPropertyLink] = useState<string>("")
  /**
   * Given the selected dropdown values, return the property to be saved.
   * @returns {Object} newObj - Property to be saved.
   */
  const formatData = (id: string, model_year?: string, url?: string) => {
    if (selectedDatasetPropertyType === OBJECT_TYPE_STR) {
      return
    }

    // Save new property object
    let newObj: any = {}
    newObj['type'] = selectedDatasetPropertyType
    newObj['category'] = selectedCategory
    newObj['property_link'] = propertyLink

    if (selectedDatasetPropertyType === PACKAGE_TYPE_STR) {
      /**
       * We display the name of the package with program name and model year separated by a space. But, the id
       * has a hyphen between the program name and model year, because this is the way Adas Programs App stores
       * the programs.
       */
      newObj['name'] = `${selectedAdasProgramName} ${selectedModelYear}`
      // meta
      const metaData: any = {}
      const modelYear = model_year !== undefined ? model_year : selectedModelYear
      metaData['modelYear'] = model_year
      metaData['programName'] = selectedAdasProgramName
      // loop through adasProgramData and find a match of this program name and model year
      let adasProgramItemData: any = {}
      adasProgramsData.forEach((element: any) => {
        if (element?.data?.programName === selectedAdasProgramName && element?.data?.modelYear === modelYear) {
          adasProgramItemData = element
        }
      })
      metaData['adasProgramId'] = adasProgramItemData.adasProgramId
      newObj['metaData'] = metaData
    } else if (selectedDatasetPropertyType === USE_CASE_TYPE_STR) {
      newObj['name'] = id
      newObj['property_link'] = url ?? ""
    } else if (selectedDatasetPropertyType === CAPABILITY_TYPE_STR) {
      newObj['name'] = id
      newObj['property_link'] = url ?? ""
    } else {
      newObj['name'] = id
    }

    return newObj
  }


  const [optionsList, setOptionsList] = useState<string[]>(getOptionsListForType(props.propertyType))

  const [selectedModelYear, setSelectedModelYear] = useState<string>(props.data?.rowData?.metaData?.modelYear ?? "")
  const [selectedAdasProgramName, setSelectedAdasProgramName] = useState<string>(props.data?.rowData?.metaData?.programName ?? "")
  const [adasProgramsToModelYears, setAdasProgramsToModelYears] = useState<any>({})
  const [adasProgramsData, setAdasProgramsData] = useState([])

  const packagesDataFetchHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchAdasPrograms)
  const [isLoading, setIsLoading] = useState(false)
  const fetchPackageData = () => {
    setIsLoading(true)
    packagesDataFetchHandle().then((data) => {
      const _adasProgramsToModelYears: any = {}
      data.forEach((element: any) => {
        const elementData = element?.data
        const programName = elementData?.programName
        const modelYear = elementData?.modelYear
        if (programName in _adasProgramsToModelYears) {
          _adasProgramsToModelYears[programName].push(modelYear)
        } else {
          _adasProgramsToModelYears[programName] = [modelYear]
        }
      })
      setAdasProgramsData(data)
      setAdasProgramsToModelYears(_adasProgramsToModelYears)
      setIsLoading(false)
    }).catch((e) => {
      console.error(e)
      toast.error("Failed to fetch Adas Programs data")
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (selectedDatasetPropertyType === PACKAGE_TYPE_STR) {
      fetchPackageData()
    } else if(selectedDatasetPropertyType !== OBJECT_TYPE_STR) {
      setOptionsList(getOptionsListForType(selectedDatasetPropertyType))
    }
  }, [])

  const [error, setError] = useState(false)

  // Use Case or Capability Item Name
  const handleItemNameChange = (event: any) => {
    setSelectedPropertyId(event.target.value)
    if (props.setBulkConfig) {
      props.setBulkConfig(formatData(event.target.value, undefined, propertyLink))
    }
  }
  // Use Case or Capability Item URL
  const handlePropertyLinkChange = (event: any) => {
    setPropertyLink(event.target.value)
    if (props.setBulkConfig) {
      props.setBulkConfig(formatData(selectedPropertyId, undefined, event.target.value))
    }
  }

  const WIDTH = 500
  // Check if the save button should be disabled based on the selected variants.
  const isSaveObjectDisabled = selectedVariant === "" ||
    (selectedVersion !== null && objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
    Object.entries(objCatToData[selectedCategory][selectedName][selectedVersion].variants).every(([_, variantId]) =>
      alreadySelectedObjects.some((object: any) => object.objectId === variantId)
    ));
  const isSaveDisabled = selectedDatasetPropertyType === OBJECT_TYPE_STR ? isSaveObjectDisabled : !adasProgramsToModelYears[selectedAdasProgramName]?.includes(selectedModelYear) && selectedDatasetPropertyType === PACKAGE_TYPE_STR
  return (
    <Loader isLoading={isLoadingObjectData}>
      <ArgosFlexbox vertical style={{justifyContent: "center", alignItems: "center"}}>

        {selectedDatasetPropertyType === OBJECT_TYPE_STR && (
          <>
            {/*Category textfield*/}
            <FormControl sx={{width: WIDTH}}>
              <InputLabel id="category-text-field" shrink={!!selectedCategory}>Category</InputLabel>
              <ArgosTextField
                label=" "
                id="category-text-field"
                value={selectedCategory}
                variant="standard"
                InputProps={{
                  readOnly: true,
                }}
              />
            </FormControl>

            {/*Name dropdown*/}
            {
            <FormControl sx={{width: WIDTH}}>
              <InputLabel id="name-select-label">Name</InputLabel>
              <Select
                labelId="name-select-label"
                id="name-select"
                value={selectedName}
                label="Name"
                IconComponent={() => null} // Removes the dropdown triangle button
                variant="standard"
                readOnly={true}
              >
                <ArgosMenuItem key={selectedName} value={selectedName}>
                  <ListItemText primary={tallyVersionsCount(selectedCategory, selectedName, alreadySelectedObjects, objCatToData) as React.ReactNode}/>
                </ArgosMenuItem>
              </Select>
            </FormControl>
            }

            {/*Version textfield*/}
            {
            <FormControl sx={{width: WIDTH}}>
              <InputLabel id="version-text-field" shrink={!!selectedVersion}>Version</InputLabel>
              <ArgosTextField
                label=" "
                id="version-text-field"
                value={selectedVersion ?? ''}
                variant="standard"
                InputProps={{
                  readOnly: true,
                }}
              />
            </FormControl>
            }

            {/*Variant dropdown*/}
            {
            <FormControl sx={{width: WIDTH}}>
              <InputLabel id="variant-select-label">Variant</InputLabel>
              <Select
                labelId="variant-select-label"
                id="variant-select"
                value={selectedVariant}
                label="Variant"
                onChange={handleVariantChange}
                variant="standard"
              >
                  {selectedVersion !== null && objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
                  Object.entries(objCatToData[selectedCategory][selectedName][selectedVersion].variants).map(([variant, variantId]: [string, string]) => (
                    <ArgosMenuItem
                      key={variant}
                      value={variant}
                      disabled={alreadySelectedObjects.some((object: any) => object.objectId === variantId)}
                    >
                      <ListItemText primary={getProfileDisplayName(variant as TPmoAndMiscProfileNames)}/>
                    </ArgosMenuItem>
                  ))
                  }
              </Select>
            </FormControl>
            }
          </>
        )}

        {selectedDatasetPropertyType == CAPABILITY_TYPE_STR &&
          <FormControl sx={{ width: WIDTH }}>
            <ArgosTextField
              id="capability-item-name"
              value={selectedPropertyId}
              label="Item Name"
              onChange={handleItemNameChange}
              variant="standard" />
            <ArgosTextField
              onBlur={(e: any) => setError(!validateURL(e.target.value))}
              helperText={error && `Invalid URL`}
              error={error}
              id="item-url"
              value={propertyLink}
              label="Item URL"
              onChange={handlePropertyLinkChange}
              variant="standard" />
          </FormControl>
        }

        {selectedDatasetPropertyType !== PACKAGE_TYPE_STR && selectedDatasetPropertyType !== USE_CASE_TYPE_STR &&
          selectedDatasetPropertyType !== OBJECT_TYPE_STR && selectedDatasetPropertyType !== CAPABILITY_TYPE_STR &&
         <FormControl sx={{width: WIDTH}}>
          <InputLabel id="property-select-label">
            Property
          </InputLabel>
          <Select
            labelId="property-select-label"
            id="property-select"
            value={selectedPropertyId}
            label="Property"
            onChange={handleOptionsChange}
            variant="standard"
          >
            {optionsList?.map((option: string) => {
              const menuItemId = option
              const menuItemValue = option
              return (
                <ArgosMenuItem disabled={selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? alreadySelectedObjects.map((obj: any) => obj.name).indexOf(option) > -1 : false} key={menuItemId} value={menuItemId}>
                  <ListItemText primary={menuItemValue}/>
                </ArgosMenuItem>
              )
            })}
          </Select>
        </FormControl>}

        {selectedDatasetPropertyType === USE_CASE_TYPE_STR &&
        <FormControl sx={{width: WIDTH}}>
          <ArgosTextField
            id="item-name"
            value={selectedPropertyId}
            label="Item Name"
            onChange={handleItemNameChange}
            variant="standard"/>
          <ArgosTextField
            id="item-url"
            onBlur={(e: any) => setError(!validateURL(e.target.value))}
            helperText={error && `Invalid URL`}
            error={error}
            value={propertyLink}
            label="Item URL"
            onChange={handlePropertyLinkChange}
            variant="standard"/>
        </FormControl>
        }

        {selectedDatasetPropertyType === PACKAGE_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="category-select-label">
            Adas Program
          </InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={selectedAdasProgramName}
            label="Adas Program"
            onChange={e => {
              setSelectedAdasProgramName(e.target.value)
              setSelectedModelYear("")
              if (props.setBulkConfig) {
                props.setBulkConfig(formatData(e.target.value, ""))
              }
            }}
            variant="standard"
          >
            {Object.keys(adasProgramsToModelYears).map((option: string) => (
              <ArgosMenuItem key={option} value={option}>
                <ListItemText
                  primary={option}
                />
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}

        {selectedDatasetPropertyType === PACKAGE_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="property-select-label">
            Model Year
          </InputLabel>
          <Select
            labelId="property-select-label"
            id="property-select"
            value={selectedModelYear}
            label="Model Year"
            onChange={e => {
              setSelectedModelYear(e.target.value)
              if (props.setBulkConfig) {
                props.setBulkConfig(formatData(e.target.value, e.target.value))
              }
            }}
            variant="standard"
          >
            {adasProgramsToModelYears[selectedAdasProgramName]?.map((option: string) => (
              <ArgosMenuItem key={option} value={option}>
                <ListItemText primary={option}/>
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}

        {/*Bottom buttons (Cancel, Add)*/}
        {!props?.hideSaveAndCancel &&
          <ArgosFlexbox>
            <ArgosButton onClick={() => props.onClose()}>
              Cancel
            </ArgosButton>
            <ArgosButton onClick={() => saveData()} disabled={isSaveDisabled}>
              OK
            </ArgosButton>
          </ArgosFlexbox>
        }
      </ArgosFlexbox>
    </Loader>
  )
}

export default EditPropertyDialog

/**
 * Finds the full object data by variant ID from the processed object data.
 * @description This function is used to retrieve the full object data, variant data, selected variant name, and variant ID   
 * @param objectId 
 * @param objCatToData 
 * @returns {IFullObjectData | null} - Returns the full object data if found, otherwise returns null.
 */
export const findObjectDataByVariantId = (objectId: string, objCatToData: IProcessedObjectData): IFullObjectData | null => {
  for (const category in objCatToData) {
    for (const name in objCatToData[category]) {
      for (const version in objCatToData[category][name]) {
        const objectData = objCatToData[category][name][version];
        let variantData = null;
        let variantId = null;
        let selectedVariant = null;

        if (objectData.object.type === "Potentially Moving Object" || objectData.object.type === "Miscellaneous") {
          for (const key in objectData.object.parameters) {
            if (objectData.object.parameters[key as TPmoAndMiscProfileNames].id === objectId) {
              variantData = objectData.object.parameters[key as TPmoAndMiscProfileNames];
              variantId = variantData.id;
              selectedVariant = key;
              break;
            }
          }
        } else if (objectData.object.type === "Sign") {
          variantData = objectData.object.signVariants.find((variant: ISignVariant) => variant.id === objectId);
          variantId = variantData?.id;
          selectedVariant = getSignVariantNameFromWidthHeight(variantData?.width, variantData?.height);
        }

        if (variantId && selectedVariant) {
          return {
            fullObjectData: objectData.object,
            variantData,
            selectedVariant,
            variantId
          };
        }
      }
    }
  }
  return null;
};